// The owner's menu draft (UC-38, #349).
//
// The owner's menu, option and discount saves wait in the draft until the
// owner publishes it; no till, catalogue page or report sees them before.
// The draft bar on Menu and Discounts (D8, D9, D21) and the Publish sheet
// (D24) read it from the server, which compares it with the latest menu
// version and flags every price far from the one it replaces. The rule lives
// in the server's core; this file only reads and words what it answers.
//
// The draft is read again after every menu and discount save
// (data.svelte.ts), so the bar's count moves with each one.

import { api } from '../api/client';
import type { components } from '../api/types';
import { fill, isArabic, message, type Language } from '../i18n/messages';
import { formatMinor } from '../format/money';

export type Draft = components['schemas']['DraftBody'];
export type MenuChange = components['schemas']['MenuChangeBody'];
export type PriceChange = components['schemas']['PriceChangeBody'];
export type Version = components['schemas']['VersionBody'];
export type PriceEntry = components['schemas']['PriceEntryBody'];

const draft = $state<{ value: Draft | null; loaded: boolean; failure: string }>({
	value: null,
	loaded: false,
	failure: '',
});

export function ownerDraft(): { value: Draft | null; loaded: boolean; failure: string } {
	return draft;
}

// Each read is numbered, and only the latest one's answer is kept: a save
// reads the draft again, and an older read that answers last must not put
// the older draft back in the bar.
let reads = 0;

/** Read the draft. A read that fails keeps what was there and says so. */
export async function loadDraft(fallback: string): Promise<void> {
	const mine = ++reads;
	try {
		const { data, error } = await api.GET('/api/owner/menu/draft', {
			params: { query: { weighed: true, perKg: true } },
		});
		if (mine !== reads) return;
		if (error !== undefined || data === undefined) {
			draft.failure = error?.message ?? fallback;
			return;
		}
		draft.value = data;
		draft.failure = '';
	} catch {
		if (mine === reads) draft.failure = fallback;
	} finally {
		if (mine === reads) draft.loaded = true;
	}
}

// The waiting menu whose cancel is in its Undo. It lives here rather than
// on the page, as the Undo does (notices.svelte.ts), so a page left and
// opened again inside the ten seconds still hides it and cannot cancel it
// twice.
let cancelling = $state('');

export function cancellingVersion(): string {
	return cancelling;
}

export function setCancellingVersion(id: string): void {
	cancelling = id;
}

export function forgetDraft(): void {
	reads++;
	draft.value = null;
	draft.loaded = false;
	draft.failure = '';
	cancelling = '';
}

/** Today in the restaurant's timezone, as a day field takes it (YYYY-MM-DD). */
export function todayIn(timezone: string, now: number = Date.now()): string {
	try {
		return new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(now);
	} catch {
		// A timezone this browser does not know: no earliest day, and the
		// server still refuses a time already past.
		return '';
	}
}

/** What a publish or a cancel answered: done, a refusal the screen words, or a failure. */
export type Outcome =
	| { ok: true; version?: Version }
	| { ok: false; code: string; message: string; flagged?: PriceChange[] };

function refused(error: unknown, fallback: string): Outcome {
	const e = error as
		{ code?: string; message?: string; details?: { flagged?: unknown } } | undefined;
	const flagged = Array.isArray(e?.details?.flagged)
		? (e.details.flagged as PriceChange[])
		: undefined;
	return { ok: false, code: e?.code ?? '', message: e?.message ?? fallback, flagged };
}

/**
 * Publish the draft the owner was shown, now or at a date and a time in the
 * restaurant's timezone, with the flagged prices the owner confirmed.
 */
export async function publish(
	shown: string,
	when: { date: string; time: string } | null,
	confirmed: PriceEntry[],
	fallback: string,
): Promise<Outcome> {
	try {
		const { data, error } = await api.POST('/api/owner/menu/versions', {
			body: {
				draft: shown,
				...(when ? { effectiveDate: when.date, effectiveTime: when.time } : {}),
				confirmedPrices: confirmed,
			},
		});
		if (error !== undefined || !data) return refused(error, fallback);
		return { ok: true, version: data };
	} catch {
		return { ok: false, code: '', message: fallback };
	}
}

/** Cancel the version that waits for its time. */
export async function cancelWaiting(versionId: string, fallback: string): Promise<Outcome> {
	try {
		const { error } = await api.POST('/api/owner/menu/versions/{versionId}/cancel', {
			params: { path: { versionId } },
		});
		if (error !== undefined) return refused(error, fallback);
		return { ok: true };
	} catch {
		return { ok: false, code: '', message: fallback };
	}
}

/** Ask which of the prices a save is about to write are far from the ones they replace. */
export async function checkPrices(
	prices: PriceEntry[],
): Promise<{ flagged: PriceChange[] } | { failed: true }> {
	try {
		const { data, error } = await api.POST('/api/owner/menu/price-check', { body: { prices } });
		if (error !== undefined || !data) return { failed: true };
		return { flagged: data.flagged ?? [] };
	} catch {
		return { failed: true };
	}
}

// ---------- words ----------

/** One line of the change list: words, and an amount or a move read left to right. */
export interface ChangeLine {
	lead: string;
	move?: string;
}

export interface ChangeGroup {
	key: 'category' | 'item' | 'shopPrice' | 'group' | 'discount';
	title: string;
	lines: ChangeLine[];
}

export interface Wording {
	language: Language;
	minorDigits: number;
	/** A shop's name by its id; the id itself when the shop is gone. */
	shopName: (id: string) => string;
}

/** "1 unpublished change", and Arabic's own counting. */
export function changesPhrase(language: Language, n: number): string {
	if (n === 1) return message(language, 'draftChangesOne');
	if (n === 2) return message(language, 'draftChangesTwo');
	return fill(message(language, n <= 10 ? 'draftChangesFew' : 'draftChangesMany'), n);
}

/** A version's day and time as the owner chose them: "13 Oct at 09:00". */
export function whenPhrase(language: Language, date: string, time: string): string {
	return fill(message(language, 'draftWhen'), dayPhrase(language, date), time);
}

function dayPhrase(language: Language, date: string): string {
	const at = new Date(`${date}T00:00:00Z`);
	if (Number.isNaN(at.getTime())) return date;
	const parts = new Intl.DateTimeFormat(isArabic(language) ? 'ar-EG-u-nu-latn' : 'en-US', {
		day: 'numeric',
		month: isArabic(language) ? 'long' : 'short',
		timeZone: 'UTC',
	}).formatToParts(at);
	const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
	return `${part('day')} ${part('month')}`;
}

/** 450 seconds as "7.5 minutes", 600 as "10 minutes". */
export function minutesPhrase(language: Language, seconds: number): string {
	const minutes = seconds / 60;
	const whole = Number.isInteger(minutes);
	const text = whole ? String(minutes) : minutes.toFixed(1);
	return fill(
		message(language, whole && minutes <= 10 ? 'publishMinutesFew' : 'publishMinutes'),
		text,
	);
}

/** The restaurant's timezone as a city the owner reads: "Cairo". */
export function timezoneCity(language: Language, timezone: string): string {
	if (timezone === 'Africa/Cairo') return message(language, 'cityCairo');
	return (timezone.split('/').pop() ?? timezone).replaceAll('_', ' ');
}

/** An option's choice rule, as the Options tab words it. */
export function ruleWords(language: Language, min: number, max: number): string {
	if (min === 1 && max === 1) return message(language, 'ruleRequiredOne');
	if (min === 0 && max === 1) return message(language, 'ruleOptionalOne');
	if (min === 0) return fill(message(language, 'ruleOptionalUpTo'), max);
	if (min === max) return fill(message(language, 'ruleExactly'), min);
	return fill(message(language, 'ruleAtLeastUpTo'), min, max);
}

/** A choice's price change, with its sign: "+10.00", "-5.00", "0.00". */
export function signedMinor(minor: number, minorDigits: number): string {
	const text = formatMinor(minor, minorDigits);
	return minor > 0 ? `+${text}` : text;
}

function quoted(language: Language, name: string): string {
	return isArabic(language) ? `«${name}»` : name;
}

function discountValue(w: Wording, percent?: number | null, minor?: number | null): string {
	if (percent != null) return `${percent}%`;
	if (minor != null)
		return fill(message(w.language, 'discountsAmountRate'), formatMinor(minor, w.minorDigits));
	return '';
}

/** The lines one change makes: one per field that changed, as D24 reads them. */
function linesOf(change: MenuChange, w: Wording): ChangeLine[] {
	const { language } = w;
	const money = (n?: number | null) => (n == null ? '' : formatMinor(n, w.minorDigits));
	const fields = change.fields ?? [];
	const named =
		change.subject === 'item' || change.subject === 'choice'
			? change.name
			: quoted(language, change.name);
	switch (change.subject) {
		case 'category':
			if (!change.id) return [{ lead: message(language, 'changeOrder') }];
			if (change.change === 'added')
				return [{ lead: fill(message(language, 'changeAdded'), named) }];
			if (change.change === 'removed')
				return [{ lead: fill(message(language, 'changeRemoved'), named) }];
			return [
				...(fields.includes('name')
					? [{ lead: fill(message(language, 'changeRenamed'), named, change.oldName ?? '') }]
					: []),
				...(fields.includes('itemOrder')
					? [{ lead: fill(message(language, 'changeItemOrder'), named) }]
					: []),
			];
		case 'item':
			if (change.change === 'added')
				return [
					{ lead: fill(message(language, 'changeAddedAt'), named), move: money(change.newMinor) },
				];
			if (change.change === 'removed')
				return [{ lead: fill(message(language, 'changeRemoved'), named) }];
			return [
				...(fields.includes('name')
					? [{ lead: fill(message(language, 'changeRenamed'), named, change.oldName ?? '') }]
					: []),
				...(fields.includes('price')
					? [{ lead: `${named}:`, move: `${money(change.oldMinor)} → ${money(change.newMinor)}` }]
					: []),
				...(fields.includes('category')
					? [{ lead: fill(message(language, 'changeMoved'), named, change.category ?? '') }]
					: []),
				...(fields.includes('groups')
					? [{ lead: fill(message(language, 'changeOptions'), named) }]
					: []),
				...(fields.includes('description')
					? [{ lead: fill(message(language, 'changeDescription'), named) }]
					: []),
				...(fields.includes('photo')
					? [{ lead: fill(message(language, 'changePhoto'), named) }]
					: []),
			];
		case 'shopPrice': {
			const at = fill(
				message(language, 'priceAtShop'),
				change.name,
				w.shopName(change.shopId ?? ''),
			);
			if (change.change === 'added')
				return [
					{ lead: fill(message(language, 'changeOwnPriceSet'), at), move: money(change.newMinor) },
				];
			if (change.change === 'removed')
				return [{ lead: fill(message(language, 'changeOwnPriceCleared'), at) }];
			return [{ lead: `${at}:`, move: `${money(change.oldMinor)} → ${money(change.newMinor)}` }];
		}
		case 'group':
			if (change.change === 'added')
				return [{ lead: fill(message(language, 'changeAdded'), named) }];
			if (change.change === 'removed')
				return [{ lead: fill(message(language, 'changeRemoved'), named) }];
			return [
				...(fields.includes('name')
					? [{ lead: fill(message(language, 'changeRenamed'), named, change.oldName ?? '') }]
					: []),
				...(fields.includes('range')
					? [
							{
								lead: fill(
									message(language, 'changeRange'),
									named,
									ruleWords(language, change.newMinSelect ?? 0, change.newMaxSelect ?? 0),
									ruleWords(language, change.oldMinSelect ?? 0, change.oldMaxSelect ?? 0),
								),
							},
						]
					: []),
				...(fields.includes('choiceOrder')
					? [{ lead: fill(message(language, 'changeChoiceOrder'), named) }]
					: []),
			];
		case 'choice': {
			const inGroup = fill(message(language, 'changeChoiceIn'), named, change.group ?? '');
			// A per-kg choice's prices are per kilogram, and both say so (D24).
			const delta = (n?: number | null) => {
				if (n == null) return '';
				const text = signedMinor(n, w.minorDigits);
				return change.perKg ? fill(message(language, 'perKilo'), text) : text;
			};
			if (change.change === 'added')
				return [{ lead: fill(message(language, 'changeAdded'), inGroup) }];
			if (change.change === 'removed')
				return [{ lead: fill(message(language, 'changeRemoved'), inGroup) }];
			return [
				...(fields.includes('name')
					? [{ lead: fill(message(language, 'changeRenamed'), named, change.oldName ?? '') }]
					: []),
				...(fields.includes('price')
					? [{ lead: `${named}:`, move: `${delta(change.oldMinor)} → ${delta(change.newMinor)}` }]
					: []),
				...(fields.includes('available')
					? [
							{
								lead: fill(
									message(language, change.available ? 'changeBackOnSale' : 'changeNowSoldOut'),
									named,
								),
							},
						]
					: []),
			];
		}
		case 'discount':
			if (change.change === 'added')
				return [
					{
						lead: fill(message(language, 'changeAdded'), named),
						move: discountValue(w, change.newPercent, change.newMinor),
					},
				];
			if (change.change === 'removed')
				return [{ lead: fill(message(language, 'changeRemoved'), named) }];
			return [
				...(fields.includes('name')
					? [{ lead: fill(message(language, 'changeRenamed'), named, change.oldName ?? '') }]
					: []),
				...(fields.includes('value')
					? [
							{
								lead: `${change.name}:`,
								move: `${discountValue(w, change.oldPercent, change.oldMinor)} → ${discountValue(w, change.newPercent, change.newMinor)}`,
							},
						]
					: []),
			];
		default:
			return [];
	}
}

const ORDER: { key: ChangeGroup['key']; title: Parameters<typeof message>[1] }[] = [
	{ key: 'category', title: 'menuCategories' },
	{ key: 'item', title: 'menuItems' },
	{ key: 'shopPrice', title: 'changesShopPrices' },
	{ key: 'group', title: 'menuOptions' },
	{ key: 'discount', title: 'ownerDiscounts' },
];

/** The change list in its groups: categories, items, shop prices, options and discounts. */
export function changeGroups(changes: MenuChange[], w: Wording): ChangeGroup[] {
	return ORDER.map(({ key, title }) => ({
		key,
		title: message(w.language, title),
		lines: changes
			.filter((c) =>
				key === 'group' ? c.subject === 'group' || c.subject === 'choice' : c.subject === key,
			)
			.flatMap((c) => linesOf(c, w)),
	})).filter((group) => group.lines.length > 0);
}

/** How many changes the owner reads: one per line. */
export function lineCount(groups: ChangeGroup[]): number {
	return groups.reduce((n, g) => n + g.lines.length, 0);
}

/** A flagged price as the warning names it: "Burger at Mall:" and "55.00 → 120.00". */
export function flagLine(flag: PriceChange, w: Wording): ChangeLine {
	const at = flag.shopId
		? fill(message(w.language, 'priceAtShop'), flag.itemName, w.shopName(flag.shopId))
		: flag.itemName;
	return {
		lead: `${at}:`,
		move: `${formatMinor(flag.oldMinor, w.minorDigits)} → ${formatMinor(flag.newMinor, w.minorDigits)}`,
	};
}

/** What a publish confirms: every flagged price, at its new price. */
export function confirmations(flagged: PriceChange[]): PriceEntry[] {
	return flagged.map((f) => ({
		itemId: f.itemId,
		...(f.shopId ? { shopId: f.shopId } : {}),
		priceMinor: f.newMinor,
	}));
}
